import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Pagination from '../components/Pagination';
import apiService from '../services/api';
import wildfireLogo from '../assets/images/wildfire-logo.png';
import '../styles/StaffPages.css';

const PAGE_SIZE = 500;

const SuperuserWildfireMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const res = await apiService.getAdminWildfireMerchants();
        setMerchants(Array.isArray(res?.results) ? res.results : []);
      } catch (err) {
        console.error('Failed to load wildfire merchants:', err);
        setError('Failed to load Wildfire merchants.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return merchants;
    return merchants.filter((m) => {
      const inName = (m.name || '').toLowerCase().includes(q);
      const inId = String(m.id || '').toLowerCase().includes(q);
      const inUrl = (m.url || '').toLowerCase().includes(q);
      const inCats = (m.categories || []).some((c) => (c || '').toLowerCase().includes(q));
      return inName || inId || inUrl || inCats;
    });
  }, [merchants, query]);

  // Reset to page 1 whenever the search changes the result set
  useEffect(() => { setPage(1); }, [query]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleCopy = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1200);
    } catch (e) {
      console.error('Clipboard write failed:', e);
    }
  };

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" text="Back to Dashboard" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <h1 className="staff-page-header" style={{ margin: 0 }}>Wildfire Merchants</h1>
            <img
              src={wildfireLogo}
              alt="Wildfire"
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <p className="staff-page-subheader">
            All merchants in the Wildfire system.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', marginBottom: '16px' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, ID, URL, or category..."
              style={{
                flex: 1,
                maxWidth: '520px',
                padding: '12px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '100px',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <div style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
              {loading ? 'Loading…' : `${filtered.length.toLocaleString()} of ${merchants.length.toLocaleString()} merchants`}
            </div>
          </div>

          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}>
            {!loading && (
              <Pagination
                position="top"
                page={page}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: '64px 1fr 160px 1fr 140px',
              gap: '12px',
              padding: '14px 18px',
              backgroundColor: '#f9fafb',
              fontFamily: '"Baikal Book"',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <div>Logo</div>
              <div>Name</div>
              <div>Wildfire ID</div>
              <div>Website</div>
              <div></div>
            </div>

            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading merchants…</div>
            )}

            {!loading && paged.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '64px 1fr 160px 1fr 140px',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '12px 18px',
                  borderBottom: '1px solid #f3f4f6',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                }}
              >
                <div>
                  {m.logo_url ? (
                    <img
                      src={m.logo_url}
                      alt={m.name}
                      style={{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white' }}
                    />
                  ) : (
                    <div style={{ width: '44px', height: '44px', borderRadius: '6px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '11px' }}>
                      No logo
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: '#111827' }}>{m.name || '(unnamed)'}</div>
                  {m.categories && m.categories.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {m.categories.slice(0, 3).join(' · ')}
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#374151' }}>{m.id}</div>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.url ? (
                    <a href={m.url} target="_blank" rel="noreferrer" style={{ color: '#0369a1', textDecoration: 'none', fontSize: '13px' }}>
                      {m.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  ) : '—'}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => handleCopy(m.id)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: copiedId === m.id ? '#00C8FF' : 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '100px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '13px',
                      color: copiedId === m.id ? '#000' : '#666',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copiedId === m.id ? 'Copied!' : 'Copy ID'}
                  </button>
                </div>
              </div>
            ))}

            {!loading && filtered.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                No merchants match "{query}".
              </div>
            )}

            {!loading && (
              <Pagination
                page={page}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserWildfireMerchants;
